import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { UserPlus, Trash2, RefreshCw, Shield, Eye, Briefcase, KeyRound } from 'lucide-react';

const ROLE_COLORS = {
  superadmin: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  manager: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  viewer: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
};

const ROLE_ICONS = { superadmin: Shield, manager: Briefcase, viewer: Eye };

const RoleBadge = ({ role }) => {
  const colors = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  const Icon = ROLE_ICONS[role] || Eye;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      background: colors.bg, color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: 600,
    }}>
      <Icon size={12} /> {role}
    </span>
  );
};

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', full_name: '', role: 'manager', tenant_ids: [] });
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, tenantsRes] = await Promise.all([
        axios.get('/api/users/'),
        axios.get('/api/tenant/'),
      ]);
      setUsers(usersRes.data);
      setTenants(tenantsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUser.email) { toast.error('Email is required'); return; }
    setCreating(true);
    try {
      await axios.post('/api/users/', newUser);
      toast.success(`Invitation sent to ${newUser.email}`);
      setShowCreate(false);
      setNewUser({ email: '', full_name: '', role: 'manager', tenant_ids: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/users/${user.id}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`/api/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const handleResendInvite = async (user) => {
    try {
      await axios.post(`/api/users/${user.id}/resend-invite`);
      toast.success('Invitation resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    }
  };

  const handleResetPassword = async (user) => {
    try {
      await axios.post(`/api/users/${user.id}/reset-password`);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const handleAssignTenant = async (userId, tenantId) => {
    try {
      await axios.post(`/api/users/${userId}/tenants`, { tenant_id: tenantId });
      toast.success('Tenant assigned');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    }
  };

  const handleRemoveTenant = async (userId, tenantId) => {
    try {
      await axios.delete(`/api/users/${userId}/tenants/${tenantId}`);
      toast.success('Tenant removed');
      fetchData();
    } catch (err) {
      toast.error('Removal failed');
    }
  };

  const toggleTenantInNew = (tenantId) => {
    setNewUser(prev => ({
      ...prev,
      tenant_ids: prev.tenant_ids.includes(tenantId)
        ? prev.tenant_ids.filter(id => id !== tenantId)
        : [...prev.tenant_ids, tenantId],
    }));
  };

  if (loading) return <div style={{ padding: '32px' }}>Loading users…</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#111' }}>User Management</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
            {users.length} users · Invite new users and manage access
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: '#1e3a5f', color: 'white', border: 'none',
            borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontWeight: 600,
          }}
        >
          <UserPlus size={16} /> Invite User
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 700 }}>Invite New User</h3>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser(p => ({ ...p, email: e.target.value.toLowerCase() }))}
                  placeholder="user@example.com"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Full Name</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={e => setNewUser(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Jane Doe"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Role</label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              {tenants.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px' }}>Assign Tenants</label>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                    {tenants.map(t => (
                      <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                        <input
                          type="checkbox"
                          checked={newUser.tenant_ids.includes(t.id)}
                          onChange={() => toggleTenantInNew(t.id)}
                        />
                        <span style={{ fontSize: '13px' }}>{t.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreate(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={creating} style={{ padding: '8px 20px', borderRadius: '6px', background: '#1e3a5f', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                  {creating ? 'Sending invite…' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users table */}
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>User</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Role</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Tenants</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <React.Fragment key={user.id}>
                <tr
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#111' }}>{user.full_name || user.email}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</div>
                  </td>
                  <td style={{ padding: '14px 16px' }}><RoleBadge role={user.role} /></td>
                  <td style={{ padding: '14px 16px' }}>
                    {user.invite_accepted_at ? (
                      <span style={{ color: user.is_active ? '#059669' : '#dc2626', fontSize: '13px', fontWeight: 600 }}>
                        {user.is_active ? '● Active' : '● Inactive'}
                      </span>
                    ) : (
                      <span style={{ color: '#d97706', fontSize: '13px', fontWeight: 600 }}>⏳ Invite pending</span>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }}>
                    {user.assigned_tenants?.length > 0
                      ? user.assigned_tenants.map(t => t.name).join(', ')
                      : <span style={{ color: '#9ca3af' }}>None</span>}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                      {!user.invite_accepted_at && (
                        <button
                          title="Resend invite"
                          onClick={() => handleResendInvite(user)}
                          style={{ padding: '6px', borderRadius: '6px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', color: '#6b7280' }}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                      {user.invite_accepted_at && (
                        <button
                          title="Send password reset email"
                          onClick={() => handleResetPassword(user)}
                          style={{ padding: '6px', borderRadius: '6px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', color: '#6b7280' }}
                        >
                          <KeyRound size={14} />
                        </button>
                      )}
                      <button
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                        onClick={() => handleToggleActive(user)}
                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', fontSize: '12px', color: user.is_active ? '#dc2626' : '#059669', fontWeight: 600 }}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        title="Delete user"
                        onClick={() => handleDelete(user)}
                        style={{ padding: '6px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fff5f5', cursor: 'pointer', color: '#dc2626' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded: tenant assignment */}
                {expandedUser === user.id && (
                  <tr>
                    <td colSpan={5} style={{ background: '#f9fafb', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: '#374151' }}>Tenant Access</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {tenants.map(t => {
                          const assigned = user.assigned_tenants?.some(a => a.id === t.id);
                          return (
                            <button
                              key={t.id}
                              onClick={() => assigned ? handleRemoveTenant(user.id, t.id) : handleAssignTenant(user.id, t.id)}
                              style={{
                                padding: '5px 12px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 600,
                                background: assigned ? '#dbeafe' : 'white',
                                color: assigned ? '#1e40af' : '#6b7280',
                                border: assigned ? '1px solid #93c5fd' : '1px solid #d1d5db',
                              }}
                            >
                              {assigned ? '✓ ' : '+ '}{t.name}
                            </button>
                          );
                        })}
                      </div>
                      {tenants.length === 0 && <span style={{ color: '#9ca3af', fontSize: '13px' }}>No tenants available</span>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  No users yet. Invite one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManager;
